/**
 * OF-001C charter §B — Diagnostic version badge (dev/preview only).
 *
 * Shows in the bottom-right corner:
 *   • FE build SHA + timestamp (baked at every dev-server start)
 *   • API base URL
 *   • Backend /api/health build/version + timestamp
 *   • Current authenticated user_id / email (from /me)
 *   • Current active workspace_id
 *   • Current active clinic_id
 *
 * Visibility gate: only renders when the hostname looks like a preview
 * environment or REACT_APP_SHOW_VERSION_BADGE=1.  Production builds with
 * a non-preview hostname hide it silently.
 *
 * Read-only diagnostic — no mutation. Owner can click to copy the
 * full payload, or use it to confirm visually that FE/BE are on the
 * expected build.
 */
import React, { useEffect, useState } from 'react';
import { FE_BUILD } from '../utils/feBuildStamp';

const BADGE_VISIBLE = (() => {
  if (typeof window === 'undefined') return false;
  if (process.env.REACT_APP_SHOW_VERSION_BADGE === '1') return true;
  const host = window.location.hostname || '';
  return host.includes('preview.emergentagent.com')
    || host === 'localhost'
    || host.startsWith('127.')
    || host.startsWith('192.168.')
    || host.endsWith('.emergent.host');
})();

const VersionBadge = () => {
  const [collapsed, setCollapsed] = useState(true);
  const [beHealth, setBeHealth] = useState(null);
  const [me, setMe] = useState(null);
  const [active, setActive] = useState({ workspace_id: null, clinic_id: null });

  useEffect(() => {
    if (!BADGE_VISIBLE) return undefined;
    let cancelled = false;
    const refresh = async () => {
      try {
        const base = process.env.REACT_APP_BACKEND_URL || '';
        // Backend health
        try {
          const r = await fetch(`${base}/api/health`);
          if (r.ok) {
            const d = await r.json();
            if (!cancelled) setBeHealth(d);
          }
        } catch { /* ignore */ }
        // /me — cookie-based auth (M-C — removed dead `tok` reference
        // that threw a ReferenceError on every 5s refresh tick).
        try {
          const r2 = await fetch(`${base}/api/auth/unified/me`, {
            credentials: 'include',
          });
          if (r2.ok) {
            const d2 = await r2.json();
            if (!cancelled) setMe({ user_id: d2.user_id, email: d2.email, name: d2.name });
          } else if (!cancelled) {
            setMe({ user_id: null, email: null, name: null, http: r2.status });
          }
        } catch { /* ignore */ }
        // Active workspace / clinic
        if (!cancelled) {
          setActive({
            workspace_id: localStorage.getItem('treatx_last_active_workspace'),
            clinic_id: sessionStorage.getItem('treatx_active_clinic_id'),
          });
        }
      } catch { /* ignore */ }
    };
    refresh();
    const onIdent = () => refresh();
    window.addEventListener('treatx:identity-changed', onIdent);
    window.addEventListener('storage', onIdent);
    const id = setInterval(refresh, 5000);
    return () => {
      cancelled = true;
      window.removeEventListener('treatx:identity-changed', onIdent);
      window.removeEventListener('storage', onIdent);
      clearInterval(id);
    };
  }, []);

  if (!BADGE_VISIBLE) return null;

  // COM-FOUNDATION — surface the authoritative runtime handle in
  // the debug panel so the owner can see parity state + transitions
  // at a glance.  Falls back to '(uninitialized)' if observability
  // module hasn't booted yet (should never happen — App.js mounts
  // it at module-load, before any component renders).
  const runtime = (typeof window !== 'undefined' && window.__TREATX_RUNTIME__)
    ? window.__TREATX_RUNTIME__.dump()
    : null;
  const parityState = runtime?.parity?.state || 'UNKNOWN';
  const parityColor = {
    PARITY_OK: '#22c55e',
    FE_STALE: '#f59e0b',
    BE_STALE: '#f59e0b',
    RUNTIME_MISMATCH: '#ef4444',
    BUNDLE_SOURCE_MISMATCH: '#ef4444',
    PARTIAL_DEPLOY: '#ef4444',
    UNKNOWN: '#94a3b8',
  }[parityState] || '#94a3b8';

  const payload = {
    fe: FE_BUILD,
    api_base: process.env.REACT_APP_BACKEND_URL || '',
    be_health: beHealth,
    be_fingerprint: runtime?.backend || null,
    parity: runtime?.parity || null,
    auth_me: me,
    active,
    hash: typeof window !== 'undefined' ? window.location.hash : '',
    served_bundle: runtime?.servedBundle || null,
    last_5_transitions: (runtime?.transitions || []).slice(0, 5),
  };

  return (
    <div
      data-testid="version-badge-anchor"
      style={{
        // PRE-M-C — anchor at BOTTOM-LEFT to keep clear of the
        // provider/patient composer's [data-testid=send-btn]
        // (bottom-right zone). Outer wrapper is a transparent
        // bounding box (pointer-events:none); only the visible pill
        // re-enables clicks.
        position: 'fixed',
        left: 8,
        bottom: 8,
        zIndex: 99999,
        pointerEvents: 'none',
      }}
    >
      <div
        data-testid="version-badge"
        style={{
          background: 'rgba(15,23,42,0.92)',
          color: '#e2e8f0',
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
          fontSize: 10,
          lineHeight: 1.35,
          borderRadius: 6,
          boxShadow: '0 4px 14px rgba(0,0,0,0.25)',
          maxWidth: collapsed ? 220 : 360,
          padding: '4px 8px',
          cursor: 'pointer',
          userSelect: 'text',
          border: '1px solid rgba(148,163,184,0.4)',
          // Interactive core — the visible pill IS clickable so the
          // Owner's collapse/expand toggle still works.
          pointerEvents: 'auto',
        }}
        onClick={() => setCollapsed((c) => !c)}
        title="Click to expand / collapse · Owner-test diagnostic only"
      >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
        <span data-testid="version-badge-fe-sha" style={{ fontWeight: 600 }}>
          FE&nbsp;{FE_BUILD.sha}
          {/* A carried-over SHA identifies an EARLIER commit, so it must not be
              shown as if it were proof of the deployed revision. */}
          {FE_BUILD.revision_verified === false && (
            <span
              data-testid="version-badge-fe-sha-unverified"
              title={`Revision not verified against this build (source: ${FE_BUILD.revision_source || 'unknown'})${FE_BUILD.build_tag ? ` · build ${FE_BUILD.build_tag}` : ''}`}
              style={{ marginLeft: 4, fontWeight: 400, opacity: 0.85 }}
            >
              (unverified{FE_BUILD.build_tag ? ` · build ${String(FE_BUILD.build_tag).slice(0, 8)}` : ''})
            </span>
          )}
        </span>
        <span
          data-testid="runtime-parity-state"
          style={{
            fontSize: 9,
            background: parityColor,
            color: '#0f172a',
            padding: '1px 5px',
            borderRadius: 4,
            fontWeight: 700,
          }}
          title={runtime?.parity?.reason || ''}
        >
          {parityState}
        </span>
        <span data-testid="version-badge-be-sha" style={{ opacity: 0.85 }}>
          BE&nbsp;{(beHealth && (beHealth.version || beHealth.build_id)) || '?'}
        </span>
      </div>
      {!collapsed && (
        <pre
          data-testid="version-badge-payload"
          style={{
            margin: '4px 0 0',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-all',
            fontSize: 9.5,
          }}
        >
{JSON.stringify(payload, null, 1)}
        </pre>
      )}
      </div>
    </div>
  );
};

export default VersionBadge;
